import { lireChampsDeclares, type AppFieldsFile } from "./custom-field.js";
import { lireEcransDeclares, type AppScreensFile } from "./app-screens.js";

/* -------------------------------------------------------------------------- */
/* La fusion a trois voies d'une declaration d'app                            */
/* -------------------------------------------------------------------------- */

/**
 * CE QUE CE FICHIER FAIT. Une app MyTaki est une DECLARATION (`taki.fields.json`
 * + `taki.screens.json` + le sous-ensemble editable de sa fiche). Le bundle
 * porte la declaration de reference, son MODELE ; un client peut en posseder sa
 * COPIE ENTIERE, son INSTANCE, avec un CLICHE du modele au moment de la copie,
 * sa BASE. Quand le bundle livre un nouveau modele, ces trois voies se rejoignent
 * ici (`docs/CHANTIERS-SOCLE.md`, chantier `app-mise-a-jour-modele`).
 *
 * LA REGLE, EN UNE PHRASE : ce que le modele a change et que le client n'a pas
 * touche suit le modele ; ce que le client a touche reste au client ; les deux
 * ont change le meme point = CONFLIT, et le conflit se GARDE AU CLIENT par
 * defaut. Un client ne perd jamais sa personnalisation sans l'avoir choisie :
 * c'est l'ecran de mise a jour qui propose « garder la mienne / prendre le
 * modele », et son choix revient ici par `choix`.
 *
 * POURQUOI LA BASE EST INDISPENSABLE. Sans elle, on ne voit que deux valeurs
 * qui different, et on ne peut pas dire QUI a bouge : un libelle que le modele
 * a corrige et un libelle que le client a ecrit se ressemblent trait pour
 * trait. Le cliche coute un second exemplaire dans le document ; c'est le prix
 * d'une fusion honnete.
 *
 * CETTE FONCTION EST PURE. Elle ne lit aucun document, n'ecrit nulle part, ne
 * connait ni le SDK ni Firestore, et ne MUTE PAS ses entrees (les trois passent
 * par un aller-retour JSON avant d'etre touchees). Elle rend un resultat, la
 * liste des changements du modele appliques, la liste des conflits, et le REFUS
 * quand le resultat ne se relit pas.
 *
 * LA GRAMMAIRE DU CHEMIN, et c'est un contrat : c'est la clef de `choix` et ce
 * que l'ecran de mise a jour affiche. Un chemin est fonde sur les IDENTITES et
 * JAMAIS sur les positions : le meme point de fusion porte le meme chemin d'une
 * version a l'autre, meme si le client a reordonne ses portes.
 *
 *   `champs/jeux[contacts::person]/champs[surnom]/label`
 *   `ecrans/ecrans[principal]/portes[personnes]/label`
 *   `ecrans/ecrans[principal]/portes[personnes]/vb/core.liste/title`
 *   `fiche/menu/label`
 *
 * Une clef d'objet s'ajoute derriere un `/` ; un element de tableau s'ecrit
 * entre crochets DERRIERE le nom du tableau, avec son identite. Les identites
 * ne sont pas echappees : elles viennent de schemas qui les bornent deja
 * (minuscules, tirets, `_`), a la seule exception du trou nomme
 * `@a-decider/<porte>`, qui reste lisible tel quel.
 */

/* -------------------------------------------------------------------------- */
/* Ce que l'appelant donne et ce qu'il recoit                                 */
/* -------------------------------------------------------------------------- */

/**
 * LES TROIS VOIES ONT LA MEME FORME, et c'est voulu : la base est le modele
 * d'hier, le modele celui d'aujourd'hui, l'instance la copie du client. Les
 * champs sont `unknown` parce qu'une instance vient d'un DOCUMENT : elle a la
 * forme d'une declaration, elle n'a pas encore ete relue par les lecteurs.
 */
export interface DeclarationAFusionner {
  champs: unknown;
  ecrans: unknown;
  fiche?: unknown;
}

/** Ce que l'ecran de mise a jour a tranche, par chemin. Absent = le defaut (l'instance). */
export type ChoixDeConflit = Readonly<Record<string, "instance" | "modele">>;

/** Un changement du modele applique sans discussion : le client n'y avait pas touche. */
export interface ChangementDeModele {
  chemin: string;
  genre: "ajoute" | "retire" | "modifie";
}

/**
 * UN POINT OU LES DEUX ONT BOUGE, chacun de son cote. Les trois valeurs sont
 * rendues telles quelles (`undefined` quand la voie n'a rien a cet endroit) :
 * l'ecran de mise a jour montre ce que le client perdrait ou gagnerait, et
 * `garde` dit ce que CETTE fusion a retenu.
 */
export interface ConflitDeFusion {
  chemin: string;
  base?: unknown;
  modele?: unknown;
  instance?: unknown;
  garde: "instance" | "modele";
}

/** Le sous-ensemble de `taki.app.json` qu'un client peut renommer. */
export interface FicheDeclaree {
  name: string;
  description?: string;
  icon?: string;
  menu?: { label: string };
}

export interface FusionDeDeclarations {
  resultat: {
    champs: AppFieldsFile;
    ecrans: AppScreensFile;
    fiche?: Record<string, unknown>;
  } | null;
  changements: readonly ChangementDeModele[];
  conflits: readonly ConflitDeFusion[];
  /** Ce qui rend le resultat inutilisable tel quel (message des lecteurs), `null` sinon. */
  refus: string | null;
}

/* -------------------------------------------------------------------------- */
/* La table d'identite des declarations                                       */
/* -------------------------------------------------------------------------- */

/**
 * PAR QUOI SE SUIT UN ELEMENT DE TABLEAU, d'une version du modele a l'autre.
 *
 * C'EST LA PIECE QUI FAIT TOUT LE RESTE. Sans identite, deux tableaux ne se
 * comparent que par RANG : le modele insere une porte en deuxieme position, et
 * toutes les suivantes se retrouvent « modifiees » face a celles du client. La
 * fusion deviendrait un mur de faux conflits, et personne ne pourrait la lire.
 *
 * `chemin` est la suite des segments STRUCTURELS du tableau, SANS crochets :
 * un element de tableau ne pose pas de segment (il partage celui de son
 * tableau), une clef d'objet en pose un. `["ecrans", "ecrans", "portes"]` est
 * donc le tableau des portes de n'importe quel ecran, et
 * `["ecrans", "ecrans", "portes", "vb", "core.fiche", "cartes"]` les cartes de
 * la fiche d'une porte, quel que soit l'id de la vue.
 *
 * `null` VEUT DIRE « CE TABLEAU EST UNE FEUILLE » : une liste de chaines
 * (`vues`, les colonnes d'une vue, les filtres d'un favori) ou une pile
 * ordonnee (`tri.defaut`, le tri d'un favori) ne se suit pas element par
 * element, elle se compare EN BLOC. Decouper « la pile de tri » en niveaux
 * independants melangerait deux ordres en un troisieme que personne n'a voulu.
 *
 * `position` est le REPLI DES CARTES ANCIENNES, et d'elles seules : l'id d'une
 * carte de fiche est facultatif (`idDeCarteSchema`, app-screens.ts), donc une
 * carte ecrite avant ce chantier n'a que son rang pour se faire suivre. Le prix
 * est ecrit dans le schema : une carte inseree par le client decale toutes
 * celles du modele. Sans position donnee, une carte sans id n'a pas d'identite,
 * et son tableau redevient une feuille.
 */
export function identiteDeclaration(
  chemin: readonly string[],
  element: unknown,
  position?: number,
): string | null {
  const dernier = chemin[chemin.length - 1];
  const avant = chemin[chemin.length - 2];
  const avantAvant = chemin[chemin.length - 3];
  if (dernier === undefined) return null;

  /* Les cartes de fiche, sous le detail par vue : `vb/<id de vue>/cartes`. */
  if (dernier === "cartes" && avantAvant === "vb") {
    const id = clefTexte(element, "id");
    if (id !== null) return id;
    return position === undefined ? null : `#${position}`;
  }

  /*
   * DEUX TABLEAUX S'APPELLENT `options`, et ils ne se suivent pas pareil : le
   * catalogue de valeurs d'un champ a choix se suit par `value`, le catalogue
   * de TRI d'un ecran par la clef qu'il trie. Le segment d'avant les separe.
   * La clef de tri est PREFIXEE (`role:title`, `champ:title`) : un champ du sac
   * peut s'appeler `title` comme le role du meme nom (Contact en a un), et les
   * deux options coexistent legitimement dans le catalogue, comme le
   * `superRefine` de `appScreenSchema` les distingue deja.
   */
  if (dernier === "options") {
    if (avant === "tri") {
      const role = clefTexte(element, "role");
      if (role !== null) return `role:${role}`;
      const champ = clefTexte(element, "champ");
      return champ === null ? null : `champ:${champ}`;
    }
    return clefTexte(element, "value");
  }

  /* Le fichier de champs : un jeu se suit par sa famille et son type local. */
  if (dernier === "jeux" && avant === "champs") {
    const family = clefTexte(element, "family");
    if (family === null) return null;
    return `${family}::${clefTexte(element, "type") ?? ""}`;
  }
  /* Un champ du sac se suit par sa CLEF, qui est immuable (custom-field.ts). */
  if (dernier === "champs" && avant === "jeux") return clefTexte(element, "key");

  /* Le catalogue de colonnes de l'ECRAN. Les colonnes d'une VUE, elles, sont
     des noms nus : leur tableau est une feuille. */
  if (dernier === "colonnes" && avant === "ecrans") return clefTexte(element, "champ");

  if (dernier === "ecrans" && avant === "ecrans") return clefTexte(element, "id");
  if (dernier === "autres" && avant === "filtres") return clefTexte(element, "id");
  if (
    dernier === "vuesCustom" ||
    dernier === "portes" ||
    dernier === "favoris" ||
    dernier === "actions" ||
    dernier === "stats" ||
    dernier === "liens"
  ) {
    return clefTexte(element, "id");
  }

  return null;
}

/** La valeur texte d'une clef d'un element, ou `null` : une identite est une chaine non vide. */
function clefTexte(element: unknown, clef: string): string | null {
  if (!estRecord(element)) return null;
  const valeur = element[clef];
  return typeof valeur === "string" && valeur.length > 0 ? valeur : null;
}

/* -------------------------------------------------------------------------- */
/* Le noyau : trois JSON, un resultat                                         */
/* -------------------------------------------------------------------------- */

/**
 * UN POINT DE FUSION PEUT NE PAS EXISTER, et c'est la moitie du travail :
 * « ajoute », « retire » et « le modele a retire ce que le client a modifie »
 * sont tous des cas ou l'une des trois voies n'a RIEN a cet endroit. `undefined`
 * ne suffisait pas : il se confond avec une clef posee a `undefined`, que
 * l'aller-retour JSON efface de toute facon.
 */
type Noeud = { readonly present: false } | { readonly present: true; readonly valeur: unknown };

const ABSENT: Noeud = { present: false };

const pose = (valeur: unknown): Noeud =>
  valeur === undefined ? ABSENT : { present: true, valeur };

const estRecord = (valeur: unknown): valeur is Record<string, unknown> =>
  typeof valeur === "object" && valeur !== null && !Array.isArray(valeur);

/** L'egalite PROFONDE de deux valeurs JSON. Ni `NaN` ni cycle n'existent ici. */
function egalJson(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((valeur, i) => egalJson(valeur, b[i]));
  }
  if (estRecord(a) && estRecord(b)) {
    const clefsA = Object.keys(a);
    if (clefsA.length !== Object.keys(b).length) return false;
    return clefsA.every(
      (clef) => Object.prototype.hasOwnProperty.call(b, clef) && egalJson(a[clef], b[clef]),
    );
  }
  return false;
}

const egalNoeud = (a: Noeud, b: Noeud): boolean =>
  a.present && b.present ? egalJson(a.valeur, b.valeur) : a.present === b.present;

/** Ce qui s'accumule pendant la descente : le tranchage de l'appelant, et ce qu'on a trouve. */
interface Contexte {
  readonly choix: ChoixDeConflit;
  readonly changements: ChangementDeModele[];
  readonly conflits: ConflitDeFusion[];
}

/**
 * UN POINT DE FUSION, ET LA DESCENTE QUAND ELLE EST POSSIBLE.
 *
 * L'ORDRE DES DEUX EST CE QUI REND LA FUSION LISIBLE. On DESCEND d'abord, et on
 * ne tranche qu'en bas : un modele qui renomme une porte et un client qui change
 * son placeholder n'ont pas touche le meme point, donc il n'y a ni conflit, ni
 * « la porte a change » en bloc. Trancher en haut rendrait vrai mais illisible :
 * « ecrans a change » ne se propose pas a un client.
 *
 * ON NE DESCEND PAS QUAND UNE DES DEUX VOIES EST ABSENTE, et c'est la le second
 * cas du chantier : « le modele a retire la porte que le client a modifiee » ne
 * se decoupe pas en clefs, il se pose ENTIER. Le client doit choisir entre une
 * porte et pas de porte, pas entre douze libelles.
 */
function fusionnerNoeud(
  base: Noeud,
  modele: Noeud,
  instance: Noeud,
  chemin: string,
  segments: readonly string[],
  ctx: Contexte,
): Noeud {
  /* Rien n'a bouge nulle part : le sous-arbre entier est celui du client. */
  if (egalNoeud(base, modele) && egalNoeud(base, instance)) return instance;

  if (modele.present && instance.present && !estFeuilleEnBloc(segments)) {
    if (
      estRecord(modele.valeur) &&
      estRecord(instance.valeur) &&
      (!base.present || estRecord(base.valeur))
    ) {
      return fusionnerObjet(base, modele.valeur, instance.valeur, chemin, segments, ctx);
    }
    if (
      Array.isArray(modele.valeur) &&
      Array.isArray(instance.valeur) &&
      (!base.present || Array.isArray(base.valeur))
    ) {
      const tableauBase = base.present ? (base.valeur as unknown[]) : [];
      const idsBase = identitesDe(tableauBase, segments);
      const idsModele = identitesDe(modele.valeur, segments);
      const idsInstance = identitesDe(instance.valeur, segments);
      if (idsBase !== null && idsModele !== null && idsInstance !== null) {
        return fusionnerTableau(
          { valeurs: tableauBase, ids: idsBase },
          { valeurs: modele.valeur, ids: idsModele },
          { valeurs: instance.valeur, ids: idsInstance },
          chemin,
          segments,
          ctx,
        );
      }
    }
  }

  /* LA FEUILLE, ou le noeud qu'on a decide de poser en bloc. Quatre cas, dans
     cet ordre : le modele n'a pas bouge, le client n'a pas touche, les deux ont
     fait la meme chose, les deux ont fait autrement. */
  if (egalNoeud(base, modele)) return instance;
  if (egalNoeud(base, instance)) {
    ctx.changements.push({ chemin, genre: genreDuChangement(base, modele) });
    return modele;
  }
  if (egalNoeud(modele, instance)) return instance;

  const prendreLeModele = ctx.choix[chemin] === "modele";
  ctx.conflits.push({
    chemin,
    base: base.present ? base.valeur : undefined,
    modele: modele.present ? modele.valeur : undefined,
    instance: instance.present ? instance.valeur : undefined,
    garde: prendreLeModele ? "modele" : "instance",
  });
  return prendreLeModele ? modele : instance;
}

/**
 * LES OBJETS QU'ON NE COUPE JAMAIS EN CLEFS : la condition des CHOIX d'un lien
 * (`reference.choix`). C'est une UNION de formes (`{ champ, op, valeurs }`,
 * `{ et: [...] }`, `{ ou: [...] }`) : fusionner deux formes differentes clef par
 * clef fabriquerait un melange que le schema refuse (`champ` a cote de `et`),
 * et bloquerait la mise a jour. Une condition se compare, se garde ou se
 * reprend EN BLOC, comme une feuille.
 */
function estFeuilleEnBloc(segments: readonly string[]): boolean {
  return segments[segments.length - 1] === "choix" && segments[segments.length - 2] === "reference";
}

function genreDuChangement(base: Noeud, modele: Noeud): ChangementDeModele["genre"] {
  if (!base.present) return "ajoute";
  if (!modele.present) return "retire";
  return "modifie";
}

/**
 * UN OBJET SE FUSIONNE CLEF PAR CLEF, y compris les objets « feuilles » du
 * schema (`jeu`, `vb`, un binding, `tri`, `filtres`, `vide`, `confirmation`) :
 * ce sont des objets comme les autres, et les traiter en bloc ferait d'un
 * libelle du modele et d'un placeholder du client un conflit qui n'existe pas.
 *
 * L'ORDRE DES CLEFS SUIT L'INSTANCE, puis ce que le modele ajoute, puis ce que
 * la base avait : un objet JSON n'a pas d'ordre qui compte, mais un document
 * qu'un humain ouvre se lit mieux quand il ressemble a celui d'avant.
 */
function fusionnerObjet(
  base: Noeud,
  modele: Record<string, unknown>,
  instance: Record<string, unknown>,
  chemin: string,
  segments: readonly string[],
  ctx: Contexte,
): Noeud {
  const objetBase = base.present && estRecord(base.valeur) ? base.valeur : {};
  const clefs: string[] = [];
  for (const clef of [...Object.keys(instance), ...Object.keys(modele), ...Object.keys(objetBase)]) {
    if (!clefs.includes(clef)) clefs.push(clef);
  }
  const sortie: Record<string, unknown> = {};
  for (const clef of clefs) {
    const fusionne = fusionnerNoeud(
      pose(objetBase[clef]),
      pose(modele[clef]),
      pose(instance[clef]),
      `${chemin}/${clef}`,
      [...segments, clef],
      ctx,
    );
    if (fusionne.present) sortie[clef] = fusionne.valeur;
  }
  return { present: true, valeur: sortie };
}

/** Un tableau et les identites de ses elements, dans le meme ordre. */
interface TableauIdentifie {
  readonly valeurs: readonly unknown[];
  readonly ids: readonly string[];
}

/**
 * LES IDENTITES D'UN TABLEAU, ou `null` quand on ne sait pas le suivre.
 *
 * DEUX ELEMENTS SOUS LA MEME IDENTITE FONT RETOMBER LE TABLEAU EN FEUILLE, et
 * c'est un refus prudent plutot qu'une perte silencieuse : suivre par identite
 * suppose qu'elle en designe UN seul, et une table qui se trompe (deux options
 * de tri qui se reduisent au meme mot) ferait disparaitre la seconde. Comparer
 * en bloc rend alors un conflit lisible au lieu d'un element evapore.
 */
function identitesDe(valeurs: readonly unknown[], segments: readonly string[]): string[] | null {
  const ids: string[] = [];
  const vus = new Set<string>();
  for (const [position, element] of valeurs.entries()) {
    const id = identiteDeclaration(segments, element, position);
    if (id === null || vus.has(id)) return null;
    vus.add(id);
    ids.push(id);
  }
  return ids;
}

/**
 * UN TABLEAU A IDENTITE SE FUSIONNE ELEMENT PAR ELEMENT, et son ORDRE est celui
 * du CLIENT.
 *
 * POURQUOI L'ORDRE DU CLIENT, ET PAS CELUI DU MODELE. L'ordre des portes est
 * l'ordre du menu, l'ordre des cartes est le plan de la fiche : ce sont des
 * decisions que le client prend pour sa maison. Une mise a jour qui les remet
 * dans l'ordre du bundle deferait a chaque version un rangement que personne
 * n'a demande.
 *
 * OU ATTERRIT UN ELEMENT QUE LE MODELE AJOUTE. Juste APRES son predecesseur du
 * modele le plus proche, s'il en reste un ; au debut sinon. Le bundle qui glisse
 * une porte entre deux autres la retrouve entre les deux chez le client, meme
 * s'il en a insere trois entre-temps ; et deux portes ajoutees a la suite
 * gardent leur ordre, parce que la premiere est deja posee quand la seconde
 * cherche la sienne.
 */
function fusionnerTableau(
  base: TableauIdentifie,
  modele: TableauIdentifie,
  instance: TableauIdentifie,
  chemin: string,
  segments: readonly string[],
  ctx: Contexte,
): Noeud {
  const parId = (tableau: TableauIdentifie): Map<string, unknown> =>
    new Map(tableau.ids.map((id, i) => [id, tableau.valeurs[i]]));
  const mapBase = parId(base);
  const mapModele = parId(modele);
  const mapInstance = parId(instance);

  const ordre = [...instance.ids];
  modele.ids.forEach((id, rang) => {
    if (ordre.includes(id)) return;
    let place = 0;
    for (let avant = rang - 1; avant >= 0; avant -= 1) {
      const trouve = ordre.indexOf(modele.ids[avant] as string);
      if (trouve >= 0) {
        place = trouve + 1;
        break;
      }
    }
    ordre.splice(place, 0, id);
  });

  const noeudDe = (source: Map<string, unknown>, id: string): Noeud =>
    source.has(id) ? { present: true, valeur: source.get(id) } : ABSENT;

  const sortie: unknown[] = [];
  for (const id of ordre) {
    const fusionne = fusionnerNoeud(
      noeudDe(mapBase, id),
      noeudDe(mapModele, id),
      noeudDe(mapInstance, id),
      `${chemin}[${id}]`,
      segments,
      ctx,
    );
    if (fusionne.present) sortie.push(fusionne.valeur);
  }
  return { present: true, valeur: sortie };
}

/* -------------------------------------------------------------------------- */
/* L'entree publique                                                          */
/* -------------------------------------------------------------------------- */

/** L'aller-retour JSON : les `undefined` disparaissent, et l'entree n'est jamais mutee. */
const copieJson = (valeur: unknown): unknown =>
  valeur === undefined ? undefined : (JSON.parse(JSON.stringify(valeur)) as unknown);

const LES_TROIS_RACINES = ["champs", "ecrans", "fiche"] as const;

/**
 * FUSIONNER TROIS DECLARATIONS : le cliche de base, le modele courant du
 * bundle, l'instance du client.
 *
 * POURQUOI LE RESULTAT SE RELIT AVANT D'ETRE RENDU. Deux modifications chacune
 * valide se combinent en une declaration invalide : le modele deplace la vue
 * d'arrivee d'un ecran sur une vue que le client a retiree de `vues`, et la
 * fusion produit un ecran que les lecteurs refusent. Le dire ICI, avec le chemin
 * fautif et en francais, vaut mieux que de l'ecrire dans Firestore et de laisser
 * l'app retomber sur son modele sans que personne ne sache pourquoi.
 *
 * LES CHANGEMENTS ET LES CONFLITS SONT RENDUS DANS LES DEUX CAS, refus compris :
 * l'ecran de mise a jour doit pouvoir dire ce qui arrive ET pourquoi ca ne passe
 * pas, et laisser trancher les conflits qui debloqueront peut-etre le refus.
 */
export function fusionnerDeclarations(
  base: DeclarationAFusionner,
  modele: DeclarationAFusionner,
  instance: DeclarationAFusionner,
  choix: ChoixDeConflit = {},
): FusionDeDeclarations {
  const ctx: Contexte = { choix, changements: [], conflits: [] };
  const copie = (declaration: DeclarationAFusionner): Record<string, unknown> => ({
    champs: copieJson(declaration.champs),
    ecrans: copieJson(declaration.ecrans),
    fiche: copieJson(declaration.fiche),
  });
  const voieBase = copie(base);
  const voieModele = copie(modele);
  const voieInstance = copie(instance);

  const sortie: Record<string, unknown> = {};
  for (const racine of LES_TROIS_RACINES) {
    const fusionne = fusionnerNoeud(
      pose(voieBase[racine]),
      pose(voieModele[racine]),
      pose(voieInstance[racine]),
      racine,
      [racine],
      ctx,
    );
    if (fusionne.present) sortie[racine] = fusionne.valeur;
  }

  const refus: string[] = [];
  let champsLus: AppFieldsFile | null = null;
  let ecransLus: AppScreensFile | null = null;
  try {
    champsLus = lireChampsDeclares(sortie.champs, "fusion:champs");
  } catch (erreur) {
    refus.push(erreur instanceof Error ? erreur.message : String(erreur));
  }
  try {
    ecransLus = lireEcransDeclares(sortie.ecrans, "fusion:ecrans");
  } catch (erreur) {
    refus.push(erreur instanceof Error ? erreur.message : String(erreur));
  }

  const resultat =
    champsLus !== null && ecransLus !== null
      ? {
          champs: champsLus,
          ecrans: ecransLus,
          ...(sortie.fiche !== undefined
            ? { fiche: sortie.fiche as Record<string, unknown> }
            : {}),
        }
      : null;

  return {
    resultat,
    changements: ctx.changements,
    conflits: ctx.conflits,
    refus: refus.length > 0 ? refus.join(" | ") : null,
  };
}

/**
 * LA FICHE D'UNE APP, REDUITE A CE QU'UN CLIENT PEUT RENOMMER : le nom, la
 * description, l'icone, le libelle du menu. C'est ce que l'appelant passe comme
 * `fiche` du modele, pour que la fusion compare le meme sous-ensemble des deux
 * cotes.
 *
 * LA LISTE EST FERMEE, ET ELLE EST ECRITE A TROIS ENDROITS QUI DOIVENT DIRE LA
 * MEME CHOSE : les Security Rules (`firebase/firestore.rules`, bloc
 * `orgs/{orgId}/apps/{appId}` : `fiche.keys().hasOnly(['name', 'description',
 * 'icon', 'menu'])`), le schema du SDK (`ficheInstancieeSchema`,
 * `packages/sdk/src/apps-instanciees.ts`) et cette projection. Ce qui reste
 * dehors est ce qui IDENTIFIE l'app (`id`, `version`, `entities`,
 * `capabilities`, `menu.route`) : le client renomme ce qu'il lit, jamais ce par
 * quoi le socle la trouve.
 *
 * AUCUNE CLEF `undefined` N'EST POSEE, et ce n'est pas de la coquetterie : une
 * clef presente a `undefined` disparait de l'aller-retour JSON, donc la fusion
 * la verrait absente d'un cote et posee de l'autre selon le chemin parcouru.
 */
export function ficheDeclaree(appInfo: {
  name: string;
  description?: string;
  icon?: string;
  menu?: { label?: string };
}): FicheDeclaree {
  const fiche: FicheDeclaree = { name: appInfo.name };
  if (appInfo.description !== undefined) fiche.description = appInfo.description;
  if (appInfo.icon !== undefined) fiche.icon = appInfo.icon;
  if (appInfo.menu?.label !== undefined) fiche.menu = { label: appInfo.menu.label };
  return fiche;
}

/** Différence de déclarations : mêmes identités et égalité que la fusion. */
export interface DifferenceDeclaration {
  path: string;
  genre: 'ajoute' | 'retire' | 'modifie';
  libelle: string;
  before?: unknown;
  after?: unknown;
}

/** Les tableaux identifiés deviennent des objets ; leur ordre reste explicite. */
export function declarationIdentifiee(value: unknown, segments: readonly string[] = []): unknown {
  if (Array.isArray(value)) {
    const ids = identitesDe(value, segments);
    if (ids === null) return value.map(item => declarationIdentifiee(item, segments));
    return Object.fromEntries([['@ordre', ids], ...ids.map((id, i) => [`[${id}]`, declarationIdentifiee(value[i], segments)])]);
  }
  if (estRecord(value)) return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, declarationIdentifiee(item, [...segments, key])]));
  return value;
}

export function differenceDeclarations(before: DeclarationAFusionner, after: DeclarationAFusionner): DifferenceDeclaration[] {
  const result: DifferenceDeclaration[] = [];
  const walk = (a: unknown, b: unknown, path: string, labels: string[]): void => {
    if (egalJson(a, b)) return;
    const node = estRecord(b) ? b : estRecord(a) ? a : {};
    const label = node.label ?? node.titre ?? node.name;
    const next = typeof label === 'string' ? [...labels, label] : labels;
    if (estRecord(a) && estRecord(b)) {
      for (const key of new Set([...Object.keys(a), ...Object.keys(b)])) {
        walk(a[key], b[key], `${path}${key.startsWith('[') ? '' : path ? '/' : ''}${key}`, next);
      }
      return;
    }
    const property = path.split('/').at(-1) ?? path;
    result.push({ path, genre: a === undefined ? 'ajoute' : b === undefined ? 'retire' : 'modifie',
      libelle: next.length ? next.join(' > ') : property,
      ...(a === undefined ? {} : { before: a }), ...(b === undefined ? {} : { after: b }) });
  };
  walk(declarationIdentifiee(before), declarationIdentifiee(after), '', []);
  type Source = { champs?: { jeux?: { family: string; type?: string; champs?: { key: string; label: string }[] }[] }; ecrans?: { ecrans?: { portes?: { label: string; jeu?: { family: string; type?: string }; tranche?: unknown }[] }[] } };
  const source = after as Source, previous = before as Source;
  for (const change of result) {
    const gameId = change.path.match(/jeux\[([^\]]+)\]/)?.[1];
    const fieldId = change.path.match(/champs\[([^\]]+)\]/)?.[1];
    if (gameId) {
      const findGame = (s: Source) => s.champs?.jeux?.find(g => `${g.family}::${g.type ?? ''}` === gameId);
      const game = findGame(source) ?? findGame(previous);
      // La porte entiere nomme le jeu, pas une porte tranchee (« Mes tâches »).
      const doors = [source, previous].flatMap(s => s.ecrans?.ecrans?.flatMap(e => e.portes ?? []) ?? []).filter(p => p.jeu?.family === game?.family && p.jeu?.type === game?.type);
      const door = doors.find(p => p.tranche === undefined) ?? doors[0];
      const field = game?.champs?.find(f => f.key === fieldId) ?? findGame(previous)?.champs?.find(f => f.key === fieldId);
      change.libelle = [door?.label ?? gameId, field?.label ?? fieldId].filter(Boolean).join(' > ');
    }
    if (change.path.startsWith('fiche/')) change.libelle = 'Fiche de l’app';
  }
  return result;
}

/** Composition des différences, y compris création/édition et édition/suppression. */
export function regrouperDifferences(changes: readonly DifferenceDeclaration[]): DifferenceDeclaration[] {
  const accumulated = new Map<string, DifferenceDeclaration>();
  const tokens = (path: string): string[] => path.match(/\[[^\]]*\]|[^/\[\]]+/g) ?? [];
  const patch = (root: unknown, path: string, value: unknown): unknown => {
    const copy = root === undefined ? {} : JSON.parse(JSON.stringify(root)) as Record<string, unknown>;
    let target = copy as Record<string, unknown>;
    const parts = tokens(path);
    for (const key of parts.slice(0, -1)) {
      if (!estRecord(target[key])) target[key] = {};
      target = target[key] as Record<string, unknown>;
    }
    const key = parts.at(-1)!;
    if (value === undefined) delete target[key]; else target[key] = value;
    return copy;
  };
  const childOf = (child: string, parent: string) => child.startsWith(`${parent}/`) || child.startsWith(`${parent}[`);
  for (const change of changes) {
    const ancestor = [...accumulated.values()].find(item => childOf(change.path, item.path));
    if (ancestor) {
      ancestor.after = patch(ancestor.after, change.path.slice(ancestor.path.length).replace(/^\//, ''), change.after);
      continue;
    }
    const old = accumulated.get(change.path);
    let first = old ? old.before : change.before;
    if (old && !('before' in old)) first = undefined;
    const descendants = [...accumulated.values()].filter(item => childOf(item.path, change.path));
    for (const item of descendants.reverse()) {
      first = patch(first, item.path.slice(change.path.length).replace(/^\//, ''), item.before);
      accumulated.delete(item.path);
    }
    const next = { ...change, ...(first === undefined ? {} : { before: first }) };
    if (first === undefined) delete next.before;
    accumulated.set(change.path, next);
  }
  return [...accumulated.values()].filter(item => !egalJson(item.before, item.after)).map(item => ({ ...item,
    genre: item.before === undefined ? 'ajoute' : item.after === undefined ? 'retire' : 'modifie' }));
}
